import {
  DeviceModeType,
  deviceModes,
  screenOrientations,
  useDeviceModeContext,
} from 'client/context/DeviceModeContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckmarkIcon } from 'src/editor/shared/icons/checkmark-icon'
import { Monitor01Icon } from 'src/editor/shared/icons/monitor-01'
import { Phone01Icon } from 'src/editor/shared/icons/phone-01-icon'
import { PhoneRotate01Icon } from 'src/editor/shared/icons/phone-rotate-01-icon'
import { XCircleFill02Icon } from 'src/editor/shared/icons/x-circle-fill-02-icon'
import { BadgeDropdown } from 'src/editor/shared/ui-lib/badge-dropdown'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import {
  BadgeColorEnum,
  BadgeSizeEnum,
} from 'src/editor/shared/ui-lib/shared/badge-consts'
import { setIsPreviewMode } from 'src/editor/store/website-pages/website-pages-actions'
import { twMerge } from 'tailwind-merge'

export const PreviewNavigation = () => {
  const {
    deviceMode,
    setDeviceMode,
    screenOrientation,
    isLandscape,
    setScreenOrientation,
    isDesktop,
    isMobile,
  } = useDeviceModeContext()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const setCurrentDeviceMode = useCallback(
    (newDeviceMode: DeviceModeType) => {
      if (deviceMode !== newDeviceMode) {
        setDeviceMode(newDeviceMode)
      }
    },
    [setDeviceMode, deviceMode],
  )

  const toggleScreenOrientation = useCallback(() => {
    if (screenOrientation === screenOrientations.Portrait) {
      setScreenOrientation(screenOrientations.Landscape)
    } else {
      setScreenOrientation(screenOrientations.Portrait)
    }
  }, [screenOrientation, setScreenOrientation])

  const currentTitle = useMemo(() => {
    switch (deviceMode) {
      case deviceModes.Desktop:
        return t('settings_options.device_appearance.desktop')
      case deviceModes.Mobile:
        return t('settings_options.device_appearance.mobile')
      default:
        return t('settings_options.device_appearance.desktop')
    }
  }, [t, deviceMode])

  const handleClosePreview = useCallback(() => {
    dispatch(setIsPreviewMode(false))
    setScreenOrientation(screenOrientations.Portrait)
  }, [dispatch, setScreenOrientation])

  return (
    <nav className="absolute right-0 left-0 z-100 grid grid-cols-3 px-8 py-2">
      <div className="col-start-2 inline-flex items-center justify-center gap-4">
        <BadgeDropdown
          size={BadgeSizeEnum.Large}
          color={BadgeColorEnum.Blue}
          title={
            <div className="flex gap-2">
              <Phone01Icon className="h-5 w-5 stroke-iconPrimary" />
              <span className="text-textLink">{currentTitle}</span>
            </div>
          }
          isDisabled={false}
          classNameDropdown="p-0"
        >
          <h5
            onClick={() => setCurrentDeviceMode(deviceModes.Desktop)}
            className="flex cursor-pointer items-center justify-between p-3 hover:bg-bgSecondary"
          >
            <div className="inline-flex items-center gap-1.5 ">
              <Monitor01Icon className="h-5 w-5 fill-iconSecondary" />
              {t('settings_options.device_appearance.desktop')}
            </div>
            {isDesktop && <CheckmarkIcon />}
          </h5>
          <h5
            onClick={() => setCurrentDeviceMode(deviceModes.Mobile)}
            className="flex cursor-pointer items-center justify-between p-3 hover:bg-bgSecondary"
          >
            <div className="inline-flex items-center gap-1.5 ">
              <Phone01Icon className="h-5 w-5 stroke-iconSecondary" />
              {t('settings_options.device_appearance.mobile')}
            </div>
            {isMobile && <CheckmarkIcon />}
          </h5>
        </BadgeDropdown>
        {isMobile && (
          <PhoneRotate01Icon
            onClick={toggleScreenOrientation}
            className={twMerge(
              'h-5 w-5 cursor-pointer fill-iconTertiary hover:fill-iconPrimary',
              isLandscape && 'fill-iconPrimary',
            )}
          />
        )}
      </div>
      <div className="inline-flex items-start justify-end">
        <GhostIconButton
          onClick={handleClosePreview}
          icon={<XCircleFill02Icon />}
        />
      </div>
    </nav>
  )
}
